import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FORM_TARGET_KINDS, type FormTargetKind } from "@njbsoft/api-client";
import { useFacility } from "@/hooks/use-facilities";
import { useCreateWorkAssignment } from "@/hooks/use-work-assignments";
import {
  AssignmentForm,
  type AssignmentFormValues,
} from "@/components/work-assignments/assignment-form";
import { workAssignmentCreateErrorKey } from "@/components/work-assignments/create-error-key";
import { useToast } from "@/hooks/use-toast";
import { PageHeader, Breadcrumbs, Card } from "@/components/ui";
import { useAppStore } from "@/store";

interface NewAssignmentSearch {
  facility_id?: string;
  target_kind?: FormTargetKind;
}

export const Route = createFileRoute("/_authenticated/test-activity/assignments/new")({
  component: WorkAssignmentNewPage,
  validateSearch: (s: Record<string, unknown>): NewAssignmentSearch => ({
    facility_id:
      typeof s.facility_id === "string" && s.facility_id !== "" ? s.facility_id : undefined,
    target_kind: FORM_TARGET_KINDS.find((kind) => kind === s.target_kind),
  }),
});

/**
 * Manual-assignment create — the unscheduled-test entry: the assignment exists the
 * moment this form submits, so the field actor can record the test or
 * inspection against it immediately. The server pins the template family's
 * current published version, derives the target kind, and sets
 * origin = manual. A link may open it on a facility (`facility_id`, named
 * by its read) and one target kind (`target_kind`) — an accepted intake's
 * Schedule premises hazard assessment. A refusal reads as its own sentence:
 * `invalid_assignee` against the assignee field, every other one in the
 * form's submit line.
 */
function WorkAssignmentNewPage() {
  const { t } = useTranslation(["work-assignments", "common", "nav"]);
  const { facility_id, target_kind } = Route.useSearch();
  const orgId = useAppStore((s) => s.activeOrgId);
  const facility = useFacility(orgId, facility_id ?? "").data;
  const toasts = useToast();
  const navigate = useNavigate();
  const createMutation = useCreateWorkAssignment();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [assigneeError, setAssigneeError] = useState<string | null>(null);

  const handleSubmit = (values: AssignmentFormValues) => {
    setSubmitError(null);
    setAssigneeError(null);
    createMutation.mutate(values, {
      onSuccess: (created) => {
        toasts.success("work-assignments:new.success");
        void navigate({
          to: "/test-activity/assignments/$id",
          params: { id: created.id },
        });
      },
      onError: (err) => {
        const key = workAssignmentCreateErrorKey(err);
        if (key === "work-assignments:errors.invalidAssignee422") setAssigneeError(t(key));
        else setSubmitError(t(key));
      },
    });
  };

  return (
    <div className="mx-auto max-w-4xl p-8">
      <Breadcrumbs
        items={[
          {
            label: t("testActivity.assignments", { ns: "nav" }),
            to: "/test-activity",
            search: { tab: "assignments" },
          },
          { label: t("new.title") },
        ]}
      />
      <PageHeader title={t("new.title")} />

      <div className="mt-6">
        <Card>
          <AssignmentForm
            initial={{
              targetKind: target_kind,
              facility: facility ? { id: facility.id, name: facility.name } : undefined,
            }}
            isSubmitting={createMutation.isPending}
            submitError={submitError}
            assigneeError={assigneeError}
            onSubmit={handleSubmit}
            onCancel={() => void navigate({ to: "/test-activity", search: { tab: "assignments" } })}
          />
        </Card>
      </div>
    </div>
  );
}
